import {
  ActionReducerMapBuilder,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';

import { ThunkExtra } from '@app/store';
import {
  DashboardService,
  GetUserWidgetPartitionsParams,
  GetUserWidgetPartitionsResponse,
} from '@app/gen/dashboard';
import { DynamicEntity } from '@app/types';
import { GetManageableCompaniesResponse, UsersService } from '@app/gen/users';

import { DashboardState } from '../dashboard.state';

const getManageableCompanies = createAsyncThunk<
  GetManageableCompaniesResponse,
  undefined,
  ThunkExtra
>(
  'dashboard/getManageableCompanies',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(UsersService);

      const result = await service.getManageableCompanies();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEmployeePartitions = createAsyncThunk<
  GetUserWidgetPartitionsResponse,
  GetUserWidgetPartitionsParams,
  ThunkExtra
>(
  'dashboard/getEmployeePartitions',
  async (getUserWidgetParams, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getUserWidgetPartitions({
        params: getUserWidgetParams,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanyPartitions = createAsyncThunk<
  GetUserWidgetPartitionsResponse,
  GetUserWidgetPartitionsParams,
  ThunkExtra
>(
  'dashboard/getCompanyPartitions',
  async (
    getUserWidgetParams,
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getUserWidgetPartitions({
        params: getUserWidgetParams,
      });

      if (
        result.partitions.some((partition) => partition.type === 'organization')
      ) {
        await awaitDispatch(dispatch(getManageableCompanies()));
      }

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const partitionsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(getEmployeePartitions.pending, (state) => {
    state.employeePartitions.status = 'loading';
  });
  builder.addCase(getEmployeePartitions.fulfilled, (state, { payload }) => {
    state.employeePartitions.status = 'complete';
    state.employeePartitions.items = payload.partitions;
    state.employeePartitions.rows = payload?.grid?.rows || 1;
    state.employeePartitions.cols = payload?.grid?.cols || 1;
  });
  builder.addCase(getEmployeePartitions.rejected, (state) => {
    state.employeePartitions.status = 'failed';
  });
  builder.addCase(getCompanyPartitions.pending, (state) => {
    state.companyPartitions.status = 'loading';
  });
  builder.addCase(getCompanyPartitions.fulfilled, (state, { payload }) => {
    state.companyPartitions.status = 'complete';
    state.companyPartitions.items = payload.partitions;
    state.companyPartitions.cols = payload?.grid?.cols || 1;
    state.companyPartitions.rows = payload?.grid?.rows || 1;
  });
  builder.addCase(getCompanyPartitions.rejected, (state) => {
    state.companyPartitions.status = 'failed';
  });
  builder.addCase(getManageableCompanies.fulfilled, (state, { payload }) => {
    state.companyPartitions.manageableCompanies = payload.companies;
  });
};
